/**
 * CRYELA CART CLEANUP — assets/cryela-cart-cleanup.js
 *
 * When a product with "Supplemento foto extra" is removed from the cart,
 * Shopify leaves the associated extra-photo line item behind.
 * This script detects and removes those orphan items automatically.
 *
 * An extra item is identified by:
 *   properties['_cryela_locked'] === 'true'
 *   properties['Collegato a']    === <product title of the main item>
 *
 * An extra item is "orphaned" when no main item with:
 *   • a matching product_title
 *   • properties['Numero foto extra'] present
 * exists in the cart anymore.
 *
 * After removing orphans, the script dispatches cart:update with the
 * updated section HTML so CartItemsComponent morphs the UI without
 * a full page reload.
 */

(function () {
  'use strict';

  /* ─── Constants ─────────────────────────────────────────────── */
  var LOCKED_PROP    = '_cryela_locked';
  var LINKED_PROP    = 'Collegato a';
  var EXTRA_PROP     = 'Numero foto extra';
  var SOURCE_ID      = 'cryela-cleanup';
  var DEBOUNCE_MS    = 600;  /* wait for any in-flight cart ops to settle */

  /* ─── State ──────────────────────────────────────────────────── */
  var timer      = null;
  var inProgress = false;

  /* ─── Entry point ────────────────────────────────────────────── */
  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(run, DEBOUNCE_MS);
  }

  async function run() {
    if (inProgress) return;
    inProgress = true;
    try {
      var cart    = await getCart();
      var orphans = findOrphans(cart.items || []);
      if (!orphans.length) return;

      var updates    = buildUpdates(orphans);
      var sectionIds = getCartSectionIds();

      var body = { updates: updates };
      if (sectionIds.length) {
        body.sections      = sectionIds.join(',');
        body.sections_url  = window.location.pathname;
      }

      var res = await fetch('/cart/update.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body:    JSON.stringify(body)
      });

      if (!res.ok) return;

      var newCart = await res.json();

      /*
       * Notify the theme so CartItemsComponent morphs the cart UI.
       * CartItemsComponent (#handleCartUpdate) checks event.detail.data.sections:
       *   • If present  → calls morphSection(sectionId, html)
       *   • If absent   → calls sectionRenderer.renderSection(sectionId, {cache:false})
       * Both paths update the DOM correctly.
       */
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource:  newCart,
          sourceId:  SOURCE_ID,
          data: {
            source:    SOURCE_ID,
            itemCount: newCart.item_count,
            sections:  newCart.sections || {}
          }
        }
      }));

    } catch (err) {
      /* Silent — cleanup is best-effort */
      console.warn('[Cryela] Cart cleanup failed:', err);
    } finally {
      inProgress = false;
    }
  }

  /* ─── Orphan detection ───────────────────────────────────────── */
  function findOrphans(items) {
    /* Extra items: locked + linked to a specific product title */
    var extras = items.filter(function (item) {
      return item.properties
          && item.properties[LOCKED_PROP] === 'true'
          && item.properties[LINKED_PROP];
    });

    if (!extras.length) return [];

    /*
     * Collect titles of all MAIN items still in cart.
     * A main item is one that has the 'Numero foto extra' property.
     * We use product_title (without variant suffix) for matching.
     */
    var mainTitles = items
      .filter(function (item) {
        return item.properties && item.properties[EXTRA_PROP];
      })
      .map(function (item) {
        return (item.product_title || item.title || '').trim();
      });

    /* An extra is orphaned when its linked product is no longer in the cart */
    return extras.filter(function (extra) {
      var linked = (extra.properties[LINKED_PROP] || '').trim();
      return !mainTitles.some(function (t) { return t === linked; });
    });
  }

  /* ─── Helpers ────────────────────────────────────────────────── */
  function buildUpdates(orphans) {
    var obj = {};
    orphans.forEach(function (item) { obj[item.key] = 0; });
    return obj;
  }

  /**
   * Find all cart-items-component section IDs currently in the DOM.
   * These can be the cart page, cart drawer, or mini-cart.
   */
  function getCartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      var id = el.dataset.sectionId;
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('Cart fetch failed (' + r.status + ')');
        return r.json();
      });
  }

  /* ─── Event listeners ────────────────────────────────────────── */

  /**
   * Primary trigger: any cart:update that is NOT our own cleanup.
   */
  document.addEventListener('cart:update', function (e) {
    var sid = e.detail && e.detail.sourceId;
    var src = e.detail && e.detail.data && e.detail.data.source;
    if (sid === SOURCE_ID || src === SOURCE_ID) return; /* our own event → skip */
    schedule();
  });

  /**
   * Fallback: intercept fetch calls to cart mutation endpoints.
   * Catches cases where cart:update is NOT dispatched (e.g. third-party
   * apps or the cart-page form submitted without JS).
   */
  var _nativeFetch = window.fetch;
  window.fetch = function (url) {
    var result = _nativeFetch.apply(this, arguments);

    if (typeof url === 'string' && /\/cart\/(change|update|clear)\.js/.test(url)) {
      /* We only peek at ok-status; the body is NOT consumed here */
      result.then(function (response) {
        if (response.ok) schedule();
      }).catch(function () { /* ignore */ });
    }

    return result; /* always return the original Promise untouched */
  };

  /**
   * Safety net: run once on page load in case orphans were left by a
   * previous session or a page reload mid-checkout.
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setTimeout(run, 1200);
    });
  } else {
    setTimeout(run, 1200);
  }

})();
